import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { API_URL } from "../config";
import ImageSlider from "../components/ImageSlider";
import "./FaceComposer.css";

function authHeaders() {
  const token = localStorage.getItem("token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function withApiUrl(items) {
  return (items || []).map((it) => ({
    ...it,
    url: it.url && it.url.startsWith("/") ? `${API_URL}${it.url}` : it.url,
  }));
}

export default function FaceComposer() {
  const [meta, setMeta] = useState(null);                 // /composer/dimensions
  const [refsByDim, setRefsByDim] = useState({});         // todas las refs por dim
  const [visibleRefsByDim, setVisibleRefsByDim] = useState({}); // tras filtros relevancia
  const [loadingRefs, setLoadingRefs] = useState({});
  const [selection, setSelection] = useState({});
  const [prompt, setPrompt] = useState("");
  const [expanding, setExpanding] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [imageB64, setImageB64] = useState("");
  const [imageMime, setImageMime] = useState("image/png");
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // Cargar metadata + refs por dimension al montar
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const dimResp = await fetch(`${API_URL}/face-composer/dimensions`);
        if (!dimResp.ok) throw new Error("No se pueden cargar dimensiones");
        const dimData = await dimResp.json();
        if (cancelled) return;
        setMeta(dimData);
        // Defaults: rellena el unico character_type y deja el resto vacio
        const defaults = {};
        for (const d of dimData.dimensions) {
          if (d.default) defaults[d.key] = d.default;
        }
        setSelection((prev) => ({ ...defaults, ...prev }));
      } catch (e) {
        setError(`Error cargando dimensiones: ${e.message}`);
      }

      // Cargar todas las refs por cada dimension visual
      try {
        const dimResp = await fetch(`${API_URL}/face-composer/dimensions`);
        const dimData = await dimResp.json();
        const imgKeys = dimData.image_dimension_keys || [];
        const tasks = imgKeys.map(async (key) => {
          setLoadingRefs((s) => ({ ...s, [key]: true }));
          try {
            const r = await fetch(`${API_URL}/face-composer/references?dimension=${key}`);
            const d = await r.json();
            const items = withApiUrl(d.items || []);
            if (!cancelled) setRefsByDim((s) => ({ ...s, [key]: items }));
          } catch (e) {
            if (!cancelled) setRefsByDim((s) => ({ ...s, [key]: [] }));
          } finally {
            if (!cancelled) setLoadingRefs((s) => ({ ...s, [key]: false }));
          }
        });
        await Promise.all(tasks);
      } catch {
        // ignore — error principal ya mostrado arriba
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Recomputar refs visibles cuando cambia la seleccion (debounced)
  const relevanceTimer = useRef(null);
  useEffect(() => {
    if (!meta) return;
    if (relevanceTimer.current) clearTimeout(relevanceTimer.current);
    relevanceTimer.current = setTimeout(async () => {
      try {
        const r = await fetch(`${API_URL}/face-composer/relevance`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ selection }),
        });
        if (!r.ok) return;
        const data = await r.json();
        // Backend devuelve refs filtradas por dim, pero con url relativa en SQLite local
        const filtered = {};
        for (const [k, items] of Object.entries(data.refs_by_dimension || {})) {
          filtered[k] = withApiUrl(items);
        }
        setVisibleRefsByDim(filtered);
      } catch {
        // si falla, dejamos todas las refs visibles
      }
    }, 250);
    return () => relevanceTimer.current && clearTimeout(relevanceTimer.current);
  }, [selection, meta]);

  const updateValue = (key, value) => {
    setSelection((s) => ({ ...s, [key]: value }));
    setPrompt("");
    setError("");
    setInfo("");
  };

  const tier1Dims = useMemo(() => meta?.dimensions.filter((d) => d.tier === 1) || [], [meta]);
  const tier2Dims = useMemo(() => meta?.dimensions.filter((d) => d.tier === 2) || [], [meta]);

  // Si hay filtros activos, mostramos refs filtradas; si no, todas
  const refsForDim = useCallback((key) => {
    const filtered = visibleRefsByDim[key];
    if (filtered !== undefined) return filtered;
    return refsByDim[key] || [];
  }, [visibleRefsByDim, refsByDim]);

  const buildPrompt = useCallback(async () => {
    setError("");
    setInfo("");
    setExpanding(true);
    try {
      const resp = await fetch(`${API_URL}/face-composer/expand-prompt`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ selection }),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt);
      }
      const data = await resp.json();
      const generated = data.prompt || "";
      setPrompt(generated);
      setInfo(`Prompt generado (${data.tokens?.input}+${data.tokens?.output} tokens)`);
      return generated;
    } catch (e) {
      setError(`Error generando prompt: ${e.message}`);
      return "";
    } finally {
      setExpanding(false);
    }
  }, [selection]);

  const downloadImage = useCallback(() => {
    if (!imageB64) return;
    const ext = imageMime.includes("jpeg") ? "jpg" : "png";
    const a = document.createElement("a");
    a.href = `data:${imageMime};base64,${imageB64}`;
    a.download = `composer_${Date.now()}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [imageB64, imageMime]);

  const saveToLibrary = useCallback(async () => {
    if (!imageB64) return;
    setSaving(true);
    setError("");
    try {
      const resp = await fetch(`${API_URL}/face-composer/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ image_b64: imageB64, prompt }),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt);
      }
      const data = await resp.json();
      setSavedId(data.id);
      setInfo(`Guardado en biblioteca como ${data.id}`);
    } catch (e) {
      setError(`Error guardando: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }, [imageB64, prompt]);

  const generateImage = useCallback(async () => {
    setError("");
    setInfo("");
    let effectivePrompt = prompt.trim();
    if (!effectivePrompt) {
      effectivePrompt = (await buildPrompt()).trim();
      if (!effectivePrompt) return;
    }
    setGenerating(true);
    setImageB64("");
    try {
      const resp = await fetch(`${API_URL}/face-composer/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ prompt: effectivePrompt }),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt);
      }
      const data = await resp.json();
      setImageB64(data.image_b64);
      setImageMime(data.mime_type || "image/png");
      setSavedId("");
      setInfo(`Imagen generada con ${data.model}`);
    } catch (e) {
      setError(`Error generando imagen: ${e.message}`);
    } finally {
      setGenerating(false);
    }
  }, [prompt, buildPrompt]);

  const busy = expanding || generating || saving;

  if (!meta) {
    return (
      <div className="composer">
        <header className="composer-header">
          <h1>Face Composer <span className="composer-badge">Beta v1.38</span></h1>
        </header>
        <div className="composer-placeholder">{error || "Cargando..."}</div>
      </div>
    );
  }

  return (
    <div className="composer">
      <header className="composer-header">
        <h1>Face Composer <span className="composer-badge">Beta v1.38</span></h1>
        <p>Compositor modular de retratos hyperrealistas. 13 dimensiones con reglas de relevancia.</p>
      </header>

      <div className={`composer-body${busy ? " is-busy" : ""}`} aria-busy={busy}>
        {/* COLUMNA IZQUIERDA — selectores */}
        <aside className="composer-selectors">
          <section className="composer-section">
            <h2>Tier 1 — Identidad</h2>
            <div className="composer-dropdowns">
              {tier1Dims.filter((d) => d.type === "text").map((d) => (
                <label className="composer-dropdown" key={d.key}>
                  <span>{d.label}</span>
                  <select
                    value={selection[d.key] || ""}
                    onChange={(e) => updateValue(d.key, e.target.value)}
                  >
                    <option value="">— sin elegir —</option>
                    {(d.options || []).map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            {tier1Dims.filter((d) => d.type === "image").map((d) => (
              <ImageSlider
                key={d.key}
                label={d.label}
                items={refsForDim(d.key)}
                selectedId={selection[d.key] || null}
                onSelect={(id) => updateValue(d.key, id)}
                loading={loadingRefs[d.key]}
                emptyText="Sin referencias para esta combinacion"
              />
            ))}
          </section>

          <section className="composer-section">
            <h2>Tier 2 — Apariencia</h2>
            <div className="composer-dropdowns">
              {tier2Dims.filter((d) => d.type === "text").map((d) => (
                <label className="composer-dropdown" key={d.key}>
                  <span>{d.label}</span>
                  <select
                    value={selection[d.key] || ""}
                    onChange={(e) => updateValue(d.key, e.target.value)}
                  >
                    <option value="">— sin elegir —</option>
                    {(d.options || []).map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            {tier2Dims.filter((d) => d.type === "image").map((d) => (
              <ImageSlider
                key={d.key}
                label={d.label}
                items={refsForDim(d.key)}
                selectedId={selection[d.key] || null}
                onSelect={(id) => updateValue(d.key, id)}
                loading={loadingRefs[d.key]}
                emptyText="Sin referencias para esta combinacion"
              />
            ))}
          </section>
        </aside>

        {/* COLUMNA DERECHA — prompt + imagen */}
        <main className="composer-output">
          <section className="composer-section">
            <div className="composer-actions">
              <button className="btn-primary" onClick={buildPrompt} disabled={busy}>
                {expanding ? "Generando prompt..." : "Generar prompt"}
              </button>
              <button className="btn-secondary" onClick={generateImage} disabled={busy}>
                {generating ? "Generando imagen..." : "Generar imagen"}
              </button>
            </div>

            {error && <div className="composer-error">{error}</div>}
            {info && !error && <div className="composer-info">{info}</div>}
          </section>

          <section className="composer-section">
            <h2>Prompt (editable)</h2>
            <textarea
              className="composer-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Pulsa 'Generar prompt' para que Claude redacte el prompt a partir de tu seleccion. Puedes editarlo antes de generar la imagen."
              rows={10}
            />
          </section>

          <section className="composer-section">
            <h2>Retrato</h2>
            <div className="composer-canvas">
              {generating && <div className="composer-placeholder">Generando con Nano Banana Pro...</div>}
              {!generating && imageB64 && (
                <img src={`data:${imageMime};base64,${imageB64}`} alt="Retrato generado" />
              )}
              {!generating && !imageB64 && (
                <div className="composer-placeholder">Sin imagen todavia.</div>
              )}
            </div>
            {imageB64 && !generating && (
              <div className="composer-actions" style={{ marginTop: 10 }}>
                <button className="btn-secondary" onClick={downloadImage}>
                  Descargar
                </button>
                <button
                  className="btn-secondary"
                  onClick={saveToLibrary}
                  disabled={saving || !!savedId}
                >
                  {saving
                    ? "Guardando..."
                    : savedId
                    ? `Guardado ${savedId}`
                    : "Guardar en biblioteca"}
                </button>
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}
